import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { LayoutDashboard } from "lucide-react";
import { useStore } from "../zustand/store";
import { hitApi } from "../components/commonAction";
import appConfig from "../config/appConfig";
import "./affiliate/affiliate.css";

/* Public pricing page at /pricing.
 * Same component is also reachable via /tiers (backward-compat alias).
 * If an affiliate sends a visitor to /pricing?ref=CODE, the ?ref is preserved
 * through to /checkout/{code}?ref=CODE on click. */
export default function PublicTiers({ pageTitle }) {
    const navigate = useNavigate();
    const location = useLocation();
    const store = useStore(state => state);
    const token = store?.userData?.token;
    const cookieToken = Cookies.get('authToken');
    const isLoggedIn = !!(token && cookieToken);
    const isAdmin = store?.userData?.role === 'admin';
    const userName = store?.userData?.name || store?.userData?.email || "Account";

    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openFaq, setOpenFaq] = useState(0);
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef(null);

    useEffect(() => {
        if (!profileOpen) return;
        const handler = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [profileOpen]);

    const logoutMe = () => {
        store.updateStore("userData", {});
        Cookies.remove('authToken');
        navigate('/');
    };

    useEffect(() => {
        hitApi({ url: "billing/tiers", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setTiers(resp.data?.tiers || []);
        });
    }, []);

    const buildCheckoutUrl = (code) => `/checkout/${code}${location.search || ""}`;

    /* The middle tier in a 3-card grid gets the "most popular" highlight. */
    const popularIndex = tiers.length === 3 ? 1 : -1;

    return (
        <div className="pxl-al-shell">
            <Helmet><title>{pageTitle || appConfig.pageTitle("Pricing")}</title></Helmet>

            {/* ── Sticky Header ── */}
            <header className="pxl-al-header">
                <div className="pxl-al-header-inner">
                    <Link to="/"><img src={appConfig.appLogo} alt={appConfig.appName} className="pxl-al-header-logo" /></Link>
                    <nav className="pxl-al-header-nav">
                        <Link to="/affiliate-program" className="pxl-al-nav-link">Affiliates</Link>
                        {isLoggedIn ? (
                            <div className="pxl-pub-profile" ref={profileRef}>
                                <button
                                    type="button"
                                    className="pxl-pub-profile-btn"
                                    onClick={() => setProfileOpen(o => !o)}
                                    aria-expanded={profileOpen}
                                >
                                    <span className="pxl-pub-profile-avatar">
                                        {userName.charAt(0).toUpperCase()}
                                    </span>
                                    <span className="pxl-pub-profile-name">{userName}</span>
                                    <svg className="pxl-pub-profile-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                </button>
                                {profileOpen && (
                                    <div className="pxl-pub-profile-dropdown">
                                        <button type="button" className="pxl-pub-profile-item" onClick={() => navigate('/dashboard')}>
                                            <LayoutDashboard size={15} strokeWidth={1.8} />
                                            Dashboard
                                        </button>
                                        <div className="pxl-pub-profile-divider" />
                                        <button type="button" className="pxl-pub-profile-item pxl-pub-profile-item--logout" onClick={logoutMe}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <Link to="/" className="pxl-al-nav-link">Log in</Link>
                                <Link to="/" className="pxlCommon-btn pxl-al-header-btn">Get started</Link>
                            </>
                        )}
                    </nav>
                </div>
            </header>

            {/* ── Hero ── */}
            <section className="pxl-al-hero pxl-pp-hero">
                <div className="pxl-al-hero-bg-orb pxl-al-hero-bg-orb--1" />
                <div className="pxl-al-hero-bg-orb pxl-al-hero-bg-orb--2" />
                <div className="pxl-al-wrap pxl-pp-hero-inner">
                    {isLoggedIn && (
                        <div className="d-flex w-100">
                            <Link to="/account/entitlements" className="pxl-al-back-btn">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                                Back to account
                            </Link>
                        </div>
                    )}
                    <div className="pxl-al-eyebrow">
                        <span className="pxl-al-eyebrow-dot" />
                        Simple, transparent pricing
                    </div>
                    <h1 className="pxl-al-hero-h1 pxl-pp-hero-h1">
                        Pick the plan that<br />
                        <span className="pxl-al-hero-h1-accent">grows with you</span>
                    </h1>
                    <p className="pxl-al-hero-lead pxl-pp-hero-lead">
                        Every plan bundles tools across the {appConfig.appName} family — no add-ons, no surprises.
                        Start small, upgrade anytime as your work scales.
                    </p>
                    <div className="pxl-al-hero-trust pxl-pp-hero-trust">
                        <span className="pxl-al-trust-item"><IconCheck /> No credit card to browse</span>
                        <span className="pxl-al-trust-item"><IconCheck /> Cancel anytime</span>
                        <span className="pxl-al-trust-item"><IconCheck /> Secure PayPal checkout</span>
                    </div>
                </div>
            </section>

            {/* ── Plans grid ── */}
            <section className="pxl-pp-plans-section">
                <div className="pxl-al-wrap">
                    {loading ? (
                        <PricingSkeleton />
                    ) : tiers.length === 0 ? (
                        <div className="pxl-pp-state">
                            <div className="pxl-pp-state-icon" aria-hidden="true">
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                            </div>
                            <h3>No plans are available right now</h3>
                            <p>We're putting the finishing touches on our packages. Please check back soon — or sign up to get notified when they launch.</p>
                            <Link to="/" className="pxlCommon-btn pxl-pp-state-cta">Notify me</Link>
                        </div>
                    ) : (
                        <div className={`pxl-pp-plans-grid pxl-pp-plans-grid--${tiers.length}`}>
                            {tiers.map((t, idx) => (
                                <PlanCard
                                    key={t._id}
                                    tier={t}
                                    popular={idx === popularIndex}
                                    isAdmin={isAdmin}
                                    onBuy={() => navigate(buildCheckoutUrl(t.code))}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* ── Trust strip ── */}
            <section className="pxl-pp-trust">
                <div className="pxl-al-wrap pxl-pp-trust-inner">
                    <TrustItem icon={<IconShield />} title="Secure payments" desc="PayPal handles every transaction. Your card details never touch our servers." />
                    <TrustItem icon={<IconRefresh />} title="Cancel anytime" desc="No long-term contracts. Stop your subscription with one click from your dashboard." />
                    <TrustItem icon={<IconHeadset />} title="Real human support" desc="Get help from the team who builds the product — usually within hours." />
                </div>
            </section>

            {/* ── FAQ ── */}
            <section className="pxl-pp-faq">
                <div className="pxl-al-wrap">
                    <div className="pxl-al-section-header">
                        <div className="pxl-al-section-label">Frequently asked questions</div>
                        <h2 className="pxl-al-section-h2">Quick answers</h2>
                    </div>
                    <div className="pxl-pp-faq-list">
                        {FAQ.map((q, i) => (
                            <FaqRow
                                key={i}
                                open={openFaq === i}
                                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                                question={q.q}
                                answer={q.a}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="pxl-al-footer">
                <div className="pxl-al-wrap pxl-al-footer-inner">
                    <div className="pxl-al-footer-left">
                        <Link to="/"><img src={appConfig.appLogo} alt={appConfig.appName} className="pxl-al-footer-logo" /></Link>
                        <span className="pxl-al-footer-copy">© {new Date().getFullYear()} {appConfig.appName}. All rights reserved.</span>
                    </div>
                    <div className="pxl-al-footer-links">
                        <Link to="/affiliate-program" className="pxl-al-footer-link">Affiliates</Link>
                        <Link to="/terms-conditions" className="pxl-al-footer-link">Terms</Link>
                        <Link to="/privacy-policy"   className="pxl-al-footer-link">Privacy</Link>
                        {isLoggedIn
                            ? <button type="button" className="pxl-al-footer-link" onClick={logoutMe}>Logout</button>
                            : <Link to="/" className="pxl-al-footer-link">Log in</Link>
                        }
                    </div>
                </div>
            </footer>
        </div>
    );
}

/* ── Sub-components ────────────────────────────────────────────────────────── */

function PricingSkeleton() {
    return (
        <div className="pxl-pp-plans-grid pxl-pp-plans-grid--3">
            {[0, 1, 2].map(i => (
                <div key={i} className="pxl-pp-plan" style={i === 1 ? { border: '2px solid #e8eaf2' } : {}}>
                    {/* plan name */}
                    <span className="pxl-sk" style={{ width: '45%', height: 18, display: 'block', marginBottom: 10 }} />
                    {/* tagline */}
                    <span className="pxl-sk" style={{ width: '80%', height: 12, display: 'block', marginBottom: 6 }} />
                    <span className="pxl-sk" style={{ width: '60%', height: 12, display: 'block', marginBottom: 24 }} />
                    {/* price */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 22 }}>
                        <span className="pxl-sk" style={{ width: 14, height: 18 }} />
                        <span className="pxl-sk" style={{ width: 72, height: 44 }} />
                        <span className="pxl-sk" style={{ width: 50, height: 12 }} />
                    </div>
                    {/* CTA button */}
                    <span className="pxl-sk" style={{ width: '100%', height: 44, display: 'block', borderRadius: 10, marginBottom: 24 }} />
                    {/* divider */}
                    <div style={{ height: 1, background: '#f0f1f7', marginBottom: 16 }} />
                    {/* includes label */}
                    <span className="pxl-sk" style={{ width: '40%', height: 11, display: 'block', marginBottom: 14 }} />
                    {/* feature rows */}
                    {[...Array(4)].map((_, j) => (
                        <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                            <span className="pxl-sk" style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0 }} />
                            <span className="pxl-sk" style={{ width: `${55 + j * 8}%`, height: 12 }} />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function PlanCard({ tier, popular, isAdmin, onBuy }) {
    const features = (tier.plans || []).map(b => ({
        product: b.product?.name,
        plan: b.plan?.name,
    }));
    const isRecurring = tier.billingType === "recurring";

    return (
        <div className={`pxl-pp-plan${popular ? " pxl-pp-plan--popular" : ""}`}>
            {popular && <div className="pxl-pp-plan-badge">Most popular</div>}
            <div className="pxl-pp-plan-name">{tier.name}</div>
            <div className="pxl-pp-plan-tagline">{tier.description || "Everything you need to get started."}</div>

            <div className="pxl-pp-plan-price">
                <span className="pxl-pp-plan-currency">{tier.currency}</span>
                <span className="pxl-pp-plan-amount">{Number(tier.price).toFixed(0)}</span>
                <span className="pxl-pp-plan-period">
                    {isRecurring ? `/ ${tier.billingInterval}` : "one-time"}
                </span>
            </div>

            <button
                type="button"
                onClick={isAdmin ? undefined : onBuy}
                disabled={isAdmin}
                title={isAdmin ? 'Admin accounts cannot purchase plans' : undefined}
                className={`pxlCommon-btn pxl-pp-plan-cta${popular ? " pxl-pp-plan-cta--popular" : ""}${isAdmin ? " pxl-pp-plan-cta--disabled" : ""}`}
            >
                {isAdmin ? 'Not available for admin' : `Get ${tier.name}`}
            </button>

            <div className="pxl-pp-plan-divider" />

            <div className="pxl-pp-plan-includes-label">What's included</div>
            <ul className="pxl-pp-plan-features">
                {features.length === 0 ? (
                    <li className="pxl-pp-plan-feature pxl-pp-plan-feature--empty">No products bundled yet.</li>
                ) : (
                    features.map((f, i) => (
                        <li key={i} className="pxl-pp-plan-feature">
                            <span className="pxl-pp-plan-feature-check"><IconCheck /></span>
                            <span>
                                <strong>{f.product}</strong>
                                {f.plan && <span className="pxl-pp-plan-feature-plan"> — {f.plan}</span>}
                            </span>
                        </li>
                    ))
                )}
            </ul>
        </div>
    );
}

function TrustItem({ icon, title, desc }) {
    return (
        <div className="pxl-pp-trust-item">
            <div className="pxl-pp-trust-icon">{icon}</div>
            <div className="pxl-pp-trust-title">{title}</div>
            <div className="pxl-pp-trust-desc">{desc}</div>
        </div>
    );
}

function FaqRow({ open, onToggle, question, answer }) {
    return (
        <div className={`pxl-pp-faq-row${open ? " pxl-pp-faq-row--open" : ""}`}>
            <button type="button" className="pxl-pp-faq-q" onClick={onToggle} aria-expanded={open}>
                <span>{question}</span>
                <span className="pxl-pp-faq-toggle" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </span>
            </button>
            <div className="pxl-pp-faq-a">{answer}</div>
        </div>
    );
}

const FAQ = [
    {
        q: "Can I switch plans later?",
        a: "Absolutely. Upgrade or downgrade anytime from your account settings — billing prorates automatically and your access updates instantly.",
    },
    {
        q: "What payment methods do you accept?",
        a: "All payments go through PayPal, which accepts every major credit/debit card plus PayPal balance. Your card details are never stored on our servers.",
    },
    {
        q: "Do you offer refunds?",
        a: "If something isn't working out within the first 14 days of a recurring plan, reach out and we'll make it right. One-time purchases are non-refundable once delivered.",
    },
    {
        q: "Is there a free trial?",
        a: "We don't have a generic trial, but every plan comes with a 14-day money-back guarantee on your first month — try it risk-free.",
    },
];

/* ── Icons ─────────────────────────────────────────────────────────────────── */
function IconCheck() {
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
}
function IconShield() {
    return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
}
function IconRefresh() {
    return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
}
function IconHeadset() {
    return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1v-6h3v4zM3 19a2 2 0 0 0 2 2h1v-6H3v4z"/></svg>;
}
